import { FC, FormEventHandler, ReactNode } from 'react'
import { enumeration } from 'common/utils/string/enumeration'
import { safeParseEventValue } from 'common/utils/forms/safeEventParseValue'
import classNames from 'classnames'
import useSearchThrottle from 'common/hooks/useSearchThrottle'
import { CloseIcon } from 'components/icons'

type Filter = {
  label: string
  searchFilter?: boolean
  options: any[]
  singleSelect?: boolean
  value: string[]
  getLabel?: (v: string) => string
  renderOption?: (v: string) => ReactNode
  onChange: (v: any[]) => void
}

export type FilterInputType = {
  placeholder?: string
  disabled?: boolean
  onClear?: () => void
  filters: Filter[]
  setPage?: (newPage: number) => void
  value?: string
  onChange: (value: string) => void
}

const FilterInput: FC<FilterInputType> = ({
  disabled,
  filters,
  onChange,
  onClear,
  placeholder,
  setPage,
  value,
}) => {
  const { searchInput, setSearchInput } = useSearchThrottle(
    value || '',
    onChange,
  )
  const onChangeText: FormEventHandler = (e) => {
    setSearchInput(safeParseEventValue(e as any))
  }

  const activeFilters = filters.flatMap((filter) =>
    filter.value.map((item) => ({
      label: filter.getLabel || filter.renderOption,
      onDelete: () => {
        setPage?.(1)
        filter.onChange(filter.value.filter((val) => val !== item))
      },
      value: item,
    })),
  )

  const hasActiveFilters = activeFilters.length > 0

  const handleClearAll = () => {
    if (onClear) {
      onClear()
      return
    }
    filters.forEach((filter) => {
      setPage?.(1)
      filter.onChange([])
    })
  }

  return (
    <div
      className={classNames('filter-input position-relative input-container', {
        'input-container--with-right-icon': hasActiveFilters,
      })}
    >
      <div className='d-flex flex-wrap align-items-center gap-1 mui-root w-100'>
        {activeFilters.map((chip, index) => (
          <span
            key={index}
            className='filter-input__chip d-inline-flex align-items-center gap-1 ps-4 pe-2'
          >
            <span>
              {chip.label
                ? chip.label(chip.value)
                : enumeration.get(chip.value)}
            </span>
            {!disabled && (
              <span
                className='chip-icon fa fa-times cursor-pointer color-light700'
                onClick={chip.onDelete}
              />
            )}
          </span>
        ))}
        <input
          disabled={disabled}
          className='filter-input__search flex-1 border-0 bg-transparent w-100'
          placeholder={hasActiveFilters ? '' : placeholder}
          value={searchInput}
          onChange={onChangeText}
        />
      </div>
      {hasActiveFilters && (
        <div
          onClick={handleClearAll}
          className='cursor-pointer input-icon icon-right'
        >
          <CloseIcon width={16} height={16} />
        </div>
      )}
    </div>
  )
}

export default FilterInput
